import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import apiService from '../services/api';
import { AuthErrorHandler, AuthError } from '../utils/errorHandler';

interface User {
  id: string;
  email: string;
  role: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingIn: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; authError?: AuthError }>;
  logout: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasStoredToken: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true); // For initial auth check
  const [isLoggingIn, setIsLoggingIn] = useState(false); // For login button state
  




  const validateTokenFallback = useCallback(async () => {
    try {
      const email = localStorage.getItem('aletheia_email');
      if (!email) {
        throw new Error('No email stored');
      }

      // Try to get user info from a protected endpoint as a fallback
      const userInfo = await apiService.getAdminUsers();
      if (userInfo && userInfo.users) {
        const currentUser = userInfo.users.find((u: any) => u.email === email);
        if (currentUser) {
          setUser({
            id: currentUser.id,
            email: currentUser.email,
            role: currentUser.role || 'admin',
            first_name: currentUser.first_name,
            last_name: currentUser.last_name,
            display_name: currentUser.display_name,
          });
        } else {
          throw new Error('User not found');
        }
      } else {
        throw new Error('Failed to get user info');
      }
    } catch (error) {
      console.error('Fallback validation failed:', error);
      logout();
    }
  }, []);

  const validateToken = useCallback(async () => {
    try {
      const token = localStorage.getItem('aletheia_token');
      
      if (!token) {
        // No token stored, user needs to login
        setIsLoading(false);
        return;
      }

      // Try to validate the token using the dedicated endpoint
      try {
        const response = await apiService.validateToken();
        if (response.success && response.user) {
          setUser({
            id: response.user.id,
            email: response.user.email,
            role: response.user.role,
            first_name: response.user.first_name,
            last_name: response.user.last_name,
            display_name: response.user.display_name,
          });
          // Store email if not already stored
          if (!localStorage.getItem('aletheia_email')) {
            localStorage.setItem('aletheia_email', response.user.email);
          }
        } else {
          throw new Error('Invalid token response');
        }
      } catch (error: any) {
        // If the validate endpoint doesn't exist (404), try fallback method
        if (error.message && error.message.includes('404')) {
          console.log('Validate endpoint not found, using fallback method');
          await validateTokenFallback();
        } else {
          console.error('Token validation failed:', error);
          // Clear invalid token and email
          logout();
        }
      }
    } catch (error) {
      console.error('Token validation error:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  }, [validateTokenFallback]);

  // Check if user is already logged in on app start
  useEffect(() => {
    const token = localStorage.getItem('aletheia_token');
    if (token) {
      // Validate token and get user info
      validateToken();
    } else {
      setIsLoading(false);
    }
  }, [validateToken]);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string; authError?: AuthError }> => {
    try {
      setIsLoggingIn(true); // Use separate login loading state
      
      // Use the error handler for validation
      const emailValidation = AuthErrorHandler.getValidationError('email', email);
      if (emailValidation) {
        return { 
          success: false, 
          error: emailValidation.userMessage,
          authError: emailValidation
        };
      }
      
      const passwordValidation = AuthErrorHandler.getValidationError('password', password);
      if (passwordValidation) {
        return { 
          success: false, 
          error: passwordValidation.userMessage,
          authError: passwordValidation
        };
      }
      
      const response = await apiService.login(email.trim(), password);
      
      if (response.success) {
        const userData = {
          id: response.user.id,
          email: response.user.email,
          role: response.user.role,
        };
        
        // Set user state first
        setUser(userData);
        
        // Store email in localStorage
        localStorage.setItem('aletheia_email', email.trim());
        
        // Ensure the user state is set before returning success
        return { success: true };
      } else {
        return { success: false, error: 'Login failed' };
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Use the error handler to create a standardized error
      const authError = AuthErrorHandler.createError(error, 'login');
      
      return { 
        success: false, 
        error: authError.userMessage,
        authError: authError
      };
    } finally {
      setIsLoggingIn(false); // Reset login loading state
    }
  };

  const logout = () => {
    setUser(null);
    apiService.clearToken();
    localStorage.removeItem('aletheia_email');
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    return user.role === role;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  // Check if user has a valid token stored (without API call)
  const hasStoredToken = (): boolean => {
    const token = localStorage.getItem('aletheia_token');
    const email = localStorage.getItem('aletheia_email');
    return !!(token && email);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isLoggingIn,
    login,
    logout,
    hasRole,
    hasAnyRole,
    hasStoredToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
